import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ArrowDownTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import { fetchCompanies } from "@/api/fetchCompanies/fetchCompaniesApi";
import { fetchEventsLookup } from "@/modules/emailManagement/features/EmailCampaignManagement/api/fetchEventsLookup/fetchEventsLookupApi";
import { loadAllFilterOptions } from "../../utils/loadAllFilterOptions";
import type { RosterManagementReportFilters } from "../../hooks/useRosterManagementReport";

type Option = { label: string; value: string };

interface RosterManagementReportFiltersProps {
  filters: RosterManagementReportFilters;
  isExporting: boolean;
  onFilterChange: (
    key: keyof RosterManagementReportFilters,
    value: string | number[] | undefined,
  ) => void;
  onExportCsv: () => void;
  // Human-readable label for the session the roster was drilled into, carried
  // through the Sessions report link. Optional - a direct sessionIds URL has none.
  sessionLabel?: string;
}

const RosterManagementReportFilters: React.FC<
  RosterManagementReportFiltersProps
> = ({ filters, isExporting, onFilterChange, onExportCsv, sessionLabel }) => {
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm ?? "");
  const [companyValue, setCompanyValue] = useState<string[]>(
    (filters.companyIds ?? []).map(String),
  );
  // Labels for the selected companies, kept separate from search results so a
  // selected chip keeps its name even when it falls outside the current query.
  const [companySelected, setCompanySelected] = useState<Option[]>([]);
  const [companyResults, setCompanyResults] = useState<Option[]>([]);
  const [companySearch, setCompanySearch] = useState("");
  const [eventIds, setEventIds] = useState<string[]>(
    (filters.eventIds ?? []).map(String),
  );
  const [sessionDateFrom, setSessionDateFrom] = useState(
    filters.sessionDateFrom ?? "",
  );
  const [sessionDateTo, setSessionDateTo] = useState(
    filters.sessionDateTo ?? "",
  );
  const [eventOptions, setEventOptions] = useState<Option[]>([]);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const debouncedCompanySearch = useDebouncedValue(companySearch, 400);
  const isMounted = useRef(false);

  useEffect(() => {
    loadAllFilterOptions((page, pageSize) =>
      fetchEventsLookup({
        page,
        pageSize,
        sortBy: "eventName",
        sortOrder: "asc",
      }).then((response) => ({
        options: response.data.map((event) => ({
          label: event.name,
          value: String(event.id),
        })),
        totalCount: response.meta?.totalCount ?? null,
      })),
    ).then(setEventOptions);
  }, []);

  useEffect(() => {
    let active = true;
    fetchCompanies({
      searchTerm: debouncedCompanySearch || undefined,
      page: 1,
      pageSize: 50,
      sortBy: "companyName",
      sortOrder: "ASC",
    }).then((response) => {
      if (!active) return;
      setCompanyResults(
        response.data.companies.map((company) => ({
          label: company.companyName,
          value: String(company.id),
        })),
      );
    });
    return () => {
      active = false;
    };
  }, [debouncedCompanySearch]);

  // Union of selected + current results so chips always resolve a label and
  // search hits remain selectable.
  const companyOptions = useMemo(() => {
    const byValue = new Map<string, Option>();
    [...companySelected, ...companyResults].forEach((option) => {
      byValue.set(option.value, option);
    });
    return Array.from(byValue.values());
  }, [companySelected, companyResults]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onFilterChange("searchTerm", debouncedSearchTerm || undefined);
  }, [debouncedSearchTerm, onFilterChange]);

  const handleCompanyChange = (value: string[]) => {
    setCompanyValue(value);
    setCompanySelected(
      companyOptions.filter((option) => value.includes(option.value)),
    );
    onFilterChange("companyIds", value.length ? value.map(Number) : undefined);
  };

  const handleEventChange = (value: string[]) => {
    setEventIds(value);
    onFilterChange("eventIds", value.length ? value.map(Number) : undefined);
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionDateFrom(e.target.value);
    onFilterChange("sessionDateFrom", e.target.value || undefined);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionDateTo(e.target.value);
    onFilterChange("sessionDateTo", e.target.value || undefined);
  };

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setCompanyValue([]);
    setCompanySelected([]);
    setCompanySearch("");
    setEventIds([]);
    setSessionDateFrom("");
    setSessionDateTo("");
    onFilterChange("searchTerm", undefined);
    onFilterChange("companyIds", undefined);
    onFilterChange("eventIds", undefined);
    onFilterChange("sessionIds", undefined);
    onFilterChange("sessionDateFrom", undefined);
    onFilterChange("sessionDateTo", undefined);
  }, [onFilterChange]);

  const sessionFilterCount = filters.sessionIds?.length ?? 0;

  const hasActiveFilters =
    searchTerm ||
    companyValue.length ||
    eventIds.length ||
    sessionFilterCount ||
    sessionDateFrom ||
    sessionDateTo;

  return (
    <div className="mb-6">
      {sessionFilterCount > 0 && (
        <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
          <span>
            {sessionLabel && sessionFilterCount === 1 ? (
              <>
                Showing the roster for{" "}
                <span className="font-medium">{sessionLabel}</span> (from the
                Sessions report).
              </>
            ) : (
              <>
                Showing the roster for {sessionFilterCount} selected session
                {sessionFilterCount > 1 ? "s" : ""} (from the Sessions report).
              </>
            )}
          </span>
          <button
            className="inline-flex items-center gap-1 font-medium hover:underline"
            onClick={() => onFilterChange("sessionIds", undefined)}
            type="button"
          >
            <XMarkIcon className="h-4 w-4" />
            Clear
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="searchTerm">Search</Label>
          <Input
            className="mt-1.5 bg-white"
            id="searchTerm"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Name, company, event..."
            type="text"
            value={searchTerm}
          />
        </div>

        <div>
          <Label>Company</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleCompanyChange}
            onSearch={setCompanySearch}
            options={companyOptions}
            placeholder="All Companies"
            searchPlaceholder="Search companies..."
            value={companyValue}
          />
        </div>

        <div>
          <Label>Event</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleEventChange}
            options={eventOptions}
            placeholder="All Events"
            value={eventIds}
          />
        </div>

        <div>
          <Label htmlFor="sessionDateFrom">Session Date From</Label>
          <Input
            className="mt-1.5 bg-white"
            id="sessionDateFrom"
            onChange={handleDateFromChange}
            type="date"
            value={sessionDateFrom}
          />
        </div>

        <div>
          <Label htmlFor="sessionDateTo">Session Date To</Label>
          <Input
            className="mt-1.5 bg-white"
            id="sessionDateTo"
            onChange={handleDateToChange}
            type="date"
            value={sessionDateTo}
          />
        </div>

        <div className="flex items-end gap-2">
          <Button
            className="border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            disabled={!hasActiveFilters}
            onClick={handleClearFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            disabled={isExporting}
            onClick={onExportCsv}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RosterManagementReportFilters;
